/// Class which implements a bounded, FIFO cache.
/// Once the cache is full, the oldest value will be purged from the cache
export class FifoCache<T> {
  //// ===========================================================================================================
  //// Member Variables
  //// ===========================================================================================================

  // Maximum number of cache elements
  private readonly size: number;

  // Circular buffer of keys in the cache
  private readonly keyArray: string[];

  // Lookup table from cache key to cached value
  private readonly cacheLookup: Map<string, T> = new Map<string, T>();

  // Next index in keyArray to insert/overwrite
  private nextIndex = 0;

  //// ===========================================================================================================
  //// Constructors
  //// ===========================================================================================================

  public constructor(size: number) {
    this.size = size;
    this.keyArray = new Array<string>(size);
  }

  //// ===========================================================================================================
  //// Methods
  //// ===========================================================================================================

  // Returns whether the given key exists in the cache
  public has(key: string): boolean {
    return this.cacheLookup.get(key) != null;
  }

  // Returns the cached value for the key, if it exists
  public get(key: string): T {
    return this.cacheLookup.get(key) as T;
  }

  // Adds key,value pair to the cache, evicting the oldest value
  // if the cache is full
  public add(key: string, value: T): void {
    const oldKey: string = this.keyArray[this.nextIndex];
    this.cacheLookup.delete(oldKey);

    this.keyArray[this.nextIndex] = key;
    this.cacheLookup.set(key, value);
    this.nextIndex = (this.nextIndex + 1) % this.size;
  }
}
